import React, { useMemo } from 'react';
import styled from 'styled-components';

const IframeContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 1.5rem;
  text-align: center;
  padding: 2rem;
`;

/**
 * Extract Google Slides presentation ID from various URL formats:
 * - https://docs.google.com/presentation/d/ABC123/edit
 * - https://docs.google.com/presentation/d/ABC123/edit?usp=sharing
 * - https://docs.google.com/presentation/d/ABC123/pub
 * - https://docs.google.com/presentation/d/ABC123/embed
 */
const extractPresentationId = (url) => {
  if (!url) return null;
  const match = url.match(/\/presentation\/d\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
};

/**
 * Build Google Slides embed URL with options
 */
const buildEmbedUrl = (presentationId, options = {}) => {
  const {
    autoStart = false,
    loop = false,
    delayMs = 0,
    startSlide = null
  } = options;

  const params = new URLSearchParams({
    start: autoStart.toString(),
    loop: loop.toString(),
    delayms: delayMs.toString()
  });

  let url = `https://docs.google.com/presentation/d/${presentationId}/embed?${params}`;

  if (startSlide !== null && startSlide > 0) {
    url += `#slide=id.p${startSlide}`;
  }

  return url;
};

/**
 * SlidesEmbed - Display Google Slides presentations in an iframe
 *
 * @param {string} url - Google Slides share/edit URL
 * @param {boolean} autoStart - Begin slideshow automatically (default: false)
 * @param {boolean} loop - Loop back to start when finished (default: false)
 * @param {number} delayMs - Auto-advance delay in ms, 0 = manual (default: 0)
 * @param {number} startSlide - Start from specific slide number (1-indexed)
 */
const SlidesEmbed = ({
  url,
  autoStart = false,
  loop = false,
  delayMs = 0,
  startSlide = null
}) => {
  const embedUrl = useMemo(() => {
    const presentationId = extractPresentationId(url);
    if (!presentationId) return null;

    return buildEmbedUrl(presentationId, {
      autoStart,
      loop,
      delayMs,
      startSlide
    });
  }, [url, autoStart, loop, delayMs, startSlide]);

  if (!embedUrl) {
    return (
      <IframeContainer>
        <ErrorMessage>
          Virheellinen Google Slides URL
          <br />
          <small style={{ opacity: 0.7 }}>{url || 'URL puuttuu'}</small>
        </ErrorMessage>
      </IframeContainer>
    );
  }

  return (
    <IframeContainer>
      <StyledIframe
        src={embedUrl}
        allowFullScreen
        title="Google Slides Presentation"
        allow="autoplay"
      />
    </IframeContainer>
  );
};

export default SlidesEmbed;
export { extractPresentationId, buildEmbedUrl };
